import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Send, Eye, CheckCircle2, XCircle, Receipt, PoundSterling, Briefcase, MessageSquare, BadgeCheck, History } from 'lucide-react';
import api from '@/lib/api';

const KIND = {
  quote: [FileText, 'bg-slate-900 text-white'], sent: [Send, 'bg-sky-100 text-sky-700'], view: [Eye, 'bg-amber-100 text-amber-700'],
  accepted: [CheckCircle2, 'bg-emerald-100 text-emerald-700'], declined: [XCircle, 'bg-rose-100 text-rose-700'],
  invoice: [Receipt, 'bg-slate-900 text-white'], payment: [PoundSterling, 'bg-emerald-100 text-emerald-700'], paid: [BadgeCheck, 'bg-emerald-600 text-white'],
  job: [Briefcase, 'bg-slate-900 text-white'], update: [MessageSquare, 'bg-violet-100 text-violet-700'], completed: [BadgeCheck, 'bg-emerald-600 text-white'],
};
const FILTERS = [['all', 'Everything'], ['quotes', 'Quotes & views'], ['money', 'Payments'], ['jobs', 'Job updates']];
const GROUP = { quotes: ['quote', 'sent', 'view', 'accepted', 'declined'], money: ['invoice', 'payment', 'paid'], jobs: ['job', 'update', 'completed'] };
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
const time = (d) => (d.length > 10 ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');

export default function CustomerTimeline({ name }) {
  const [ev, setEv] = useState(null);
  const [f, setF] = useState('all');
  useEffect(() => { setEv(null); api.get('/customers/timeline', { params: { name } }).then(({ data }) => setEv(data)).catch(() => setEv([])); }, [name]);
  const groups = useMemo(() => {
    const out = [];
    (ev || []).filter((e) => f === 'all' || GROUP[f].includes(e.kind)).forEach((e) => {
      const d = day(e.at);
      if (out[out.length - 1]?.d !== d) out.push({ d, items: [] });
      out[out.length - 1].items.push(e);
    });
    return out;
  }, [ev, f]);

  return (
    <section className="tm-card p-5" data-testid="customer-timeline">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><History size={16} className="text-brand" /> Timeline</h3>
        <div className="flex flex-wrap gap-1.5" data-testid="timeline-filters">
          {FILTERS.map(([id, l]) => (
            <button key={id} type="button" onClick={() => setF(id)} data-testid={`timeline-filter-${id}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${f === id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:border-slate-900'}`}>{l}</button>
          ))}
        </div>
      </div>
      {ev === null && <p className="text-sm text-slate-500">Loading timeline…</p>}
      {ev && !groups.length && <p className="text-sm text-slate-500 py-2" data-testid="timeline-empty">Nothing here yet.</p>}
      <ol className="space-y-6">
        {groups.map((g) => (
          <li key={g.d}>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2" data-testid="timeline-day">{g.d}</p>
            <ul className="relative border-l border-slate-200 ml-3.5 space-y-1">
              {g.items.map((e, i) => {
                const [Icon, cls] = KIND[e.kind] || KIND.update;
                return (
                  <li key={`${e.at}-${i}`} className="relative pl-7" data-testid={`timeline-event-${e.kind}`}>
                    <span className={`absolute -left-3.5 top-2 grid place-items-center w-7 h-7 rounded-full ring-4 ring-white ${cls}`}><Icon size={13} /></span>
                    <Link to={e.link} className="flex items-start justify-between gap-3 rounded-lg px-2 py-2 hover:bg-slate-50 text-sm">
                      <span className="min-w-0">
                        <span className="font-semibold text-slate-900 block">{e.title}</span>
                        {e.sub && <span className="text-xs text-slate-500 block truncate">{e.sub}</span>}
                      </span>
                      <span className="shrink-0 text-right">
                        {e.amount ? <span className={`block font-semibold ${['payment', 'paid'].includes(e.kind) ? 'text-emerald-700' : ''}`}>{e.kind === 'payment' ? '+' : ''}{gbp(e.amount)}</span> : null}
                        <span className="text-xs text-slate-400 font-mono">{time(e.at)}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}
